import { useEffect, useMemo, useRef, useState } from "react";

/**
 * CustomAudioPlayer (UX-tuned for your intonation compare panel)
 *
 * Goals:
 * - Visually “belongs” under the user plot (same card language, light, compact)
 * - Clearly communicates: "This plays YOUR recording"
 * - Avoids the giant standalone card look
 * - Works in tight layouts (desktop + mobile)
 * - Keyboard hint: "P" to play (optional)
 */
export default function CustomAudioPlayer({
  audio,
  onTimeUpdate,
  title = "Your recording",
  helper = "Play your recording and compare the curve above.",
  showKeyboardHint = true,
  compact = true,
}) {
  const ref = useRef(null);

  const [isPlaying, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // If audio changes, reset UI state
  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
    if (ref.current) {
      ref.current.pause();
      ref.current.currentTime = 0;
      ref.current.playbackRate = rate;
    }
  }, [audio]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    const el = ref.current;
    if (!el) return;
    if (!audio) return;

    if (el.paused) {
      el.play();
    } else {
      el.pause();
    }
  };

  const setSpeed = (r) => {
    setRate(r);
    if (ref.current) ref.current.playbackRate = r;
  };

  const fmt = (t) => {
    if (!Number.isFinite(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const progressPct = useMemo(() => {
    if (!duration || !Number.isFinite(duration)) return 0;
    return Math.max(0, Math.min(100, (current / duration) * 100));
  }, [current, duration]);

  const speedOptions = useMemo(
    () => [
      { v: 1, label: "Normal" },
      { v: 0.75, label: "Slow" },
      { v: 0.5, label: "Slower" },
    ],
    []
  );

  // Compact, inline “belongs-to-plot” styling
  return (
    <div
      className={[
        // container should not look like a separate big card
        compact
          ? "w-full"
          : `w-full ${isMobile ? "px-2" : "max-w-md"} mx-auto`,
      ].join(" ")}
    >
      <div
        className={[
          // make it feel like a footer bar under the chart
          "rounded-xl border border-white/50 bg-white/60 backdrop-blur-sm shadow-sm",
          "px-3 sm:px-4 py-3",
        ].join(" ")}
      >
        {/* Hidden audio element */}
        <audio
          src={audio || undefined}
          ref={ref}
          preload="metadata"
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            setDuration(Number.isFinite(d) ? d : 0);
          }}
          onTimeUpdate={(e) => {
            const t = e.currentTarget.currentTime;
            setCurrent(t);
            onTimeUpdate?.(t);
          }}
          onEnded={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />

        {/* Top row: label + hint */}
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white flex items-center justify-center shadow-sm shrink-0">
            🎙️
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-semibold text-gray-900 truncate">{title}</div>
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-full shrink-0">
                your audio
              </span>
            </div>
            <div className="text-xs text-gray-600 leading-snug">
              {helper}
              {showKeyboardHint && (
                <span className="ml-2 text-[11px] text-gray-500">
                  {isMobile ? "" : "Shortcut: P"}
                </span>
              )}
            </div>
          </div>

          {/* Time */}
          <div className="text-xs text-gray-600 tabular-nums shrink-0">
            {fmt(current)} <span className="text-gray-400">/</span>{" "}
            {fmt(duration)}
          </div>
        </div>

        {/* Progress bar (thin, subtle) */}
        <div className="mt-2">
          <div className="h-1.5 rounded-full bg-gray-200/70 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-green-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        <div
          className={[
            "mt-3 flex items-center gap-3",
            isMobile ? "flex-col items-stretch" : "flex-row",
          ].join(" ")}
        >
          {/* Play button (primary) */}
          <button
            type="button"
            onClick={togglePlay}
            disabled={!audio}
            className={[
              "inline-flex items-center justify-center gap-2",
              "rounded-xl font-semibold transition-all",
              "border shadow-sm",
              audio
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-700/20 hover:shadow-md"
                : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed",
              isMobile ? "w-full h-11" : "h-11 px-4",
            ].join(" ")}
            title={audio ? "Play your recording" : "No recording available"}
          >
            <span className="text-lg leading-none">{isPlaying ? "⏸" : "▶️"}</span>
            <span className="text-sm">
              {isPlaying ? "Pause" : "Play"} my recording
            </span>
          </button>

          {/* Speed pill group */}
          <div
            className={[
              "flex-1",
              "rounded-xl border border-white/50 bg-white/55",
              "px-2 py-2",
              "flex items-center gap-2",
              isMobile ? "w-full justify-between" : "justify-end",
            ].join(" ")}
          >
            <span className="text-[11px] font-semibold text-gray-600 pl-1 shrink-0">
              Speed
            </span>

            <div className="flex gap-1">
              {speedOptions.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setSpeed(o.v)}
                  className={[
                    "h-8 px-3 rounded-lg text-xs font-semibold transition-all border",
                    rate === o.v
                      ? "bg-gray-900 text-white border-gray-900 shadow-sm"
                      : "bg-white/70 text-gray-700 border-white/60 hover:bg-white",
                  ].join(" ")}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Micro tip (very small, not taking space) */}
        <div className="mt-2 text-[11px] text-gray-500">
          Tip: listen for the <span className="font-semibold">rise/fall</span>{" "}
          on each word and match the narrator’s shape above.
        </div>
      </div>
    </div>
  );
}